import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import DashboardLayout from '@/components/feature/DashboardLayout';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/lib/supabase';
import { calcularDataVencimento } from '@/pages/dashboard/shared-data';

interface Condominio {
  id: string;
  nome: string;
  localizacao: string;
  uso_edificacao: string | null;
  endereco: Record<string, unknown> | null;
  sindico: Record<string, unknown> | null;
  administradora: Record<string, unknown> | null;
}

interface Pendencia {
  id: string;
  titulo: string;
  descricao: string;
  tipo: string;
  prioridade: 'alta' | 'media' | 'baixa';
  status: string;
  acao: string;
  link: string | null;
  condominioNome: string;
}

function PrioridadeBadge({ prioridade }: { prioridade: string }) {
  const styles: Record<string, string> = {
    alta: 'bg-accent-50 text-accent-700 border-accent-200',
    media: 'bg-secondary-50 text-secondary-700 border-secondary-200',
    baixa: 'bg-primary-50 text-primary-700 border-primary-200',
  };
  return (
    <span
      className={`px-2 py-0.5 rounded-full text-xs font-medium border ${styles[prioridade] || styles.baixa}`}
    >
      {prioridade}
    </span>
  );
}

function StatusBadge({ status }: { status: string }) {
  return (
    <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-accent-50 text-accent-700 border border-accent-200">
      {status}
    </span>
  );
}

function computeRegistrationPendencias(condo: Condominio): Pendencia[] {
  const pendencias: Pendencia[] = [];
  const endereco = condo.endereco as Record<string, unknown> | null;

  if (!condo.uso_edificacao) {
    pendencias.push({
      id: `reg-uso-${condo.id}`,
      titulo: `Uso da edificação não informado (${condo.nome})`,
      descricao: 'Preencha o tipo de uso da edificação (residencial, comercial, misto etc.).',
      tipo: 'Cadastro',
      prioridade: 'alta',
      status: 'pendente',
      acao: 'Completar cadastro',
      link: `/condominios/${condo.id}`,
      condominioNome: condo.nome,
    });
  }

  if (!endereco || !endereco.cep || !endereco.logradouro) {
    pendencias.push({
      id: `reg-end-${condo.id}`,
      titulo: `Endereço incompleto (${condo.nome})`,
      descricao:
        'Preencha o endereço completo do condomínio (CEP, logradouro, número, bairro, cidade, estado).',
      tipo: 'Cadastro',
      prioridade: 'alta',
      status: 'pendente',
      acao: 'Completar endereço',
      link: `/condominios/${condo.id}`,
      condominioNome: condo.nome,
    });
  }

  const sindico = condo.sindico as Record<string, unknown> | null;
  if (!sindico || !sindico.nome || !sindico.email) {
    pendencias.push({
      id: `reg-sind-${condo.id}`,
      titulo: `Dados do síndico incompletos (${condo.nome})`,
      descricao: 'Cadastre nome, e-mail e telefone do síndico responsável.',
      tipo: 'Cadastro',
      prioridade: 'media',
      status: 'pendente',
      acao: 'Cadastrar síndico',
      link: `/condominios/${condo.id}`,
      condominioNome: condo.nome,
    });
  }

  const adm = condo.administradora as Record<string, unknown> | null;
  if (!adm || !adm.nome) {
    pendencias.push({
      id: `reg-adm-${condo.id}`,
      titulo: `Administradora não informada (${condo.nome})`,
      descricao: 'Cadastre a administradora responsável pelo condomínio.',
      tipo: 'Cadastro',
      prioridade: 'baixa',
      status: 'pendente',
      acao: 'Cadastrar administradora',
      link: `/condominios/${condo.id}`,
      condominioNome: condo.nome,
    });
  }

  return pendencias;
}

function computeTorresPendencia(condo: Condominio, torresCount: number): Pendencia | null {
  if (torresCount === 0) {
    return {
      id: `torres-${condo.id}`,
      titulo: `Nenhuma torre cadastrada (${condo.nome})`,
      descricao:
        'Cadastre as torres do condomínio para distribuir itens obrigatórios e controles por bloco.',
      tipo: 'Cadastro',
      prioridade: 'alta',
      status: 'pendente',
      acao: 'Cadastrar torres',
      link: `/condominios/${condo.id}`,
      condominioNome: condo.nome,
    };
  }
  return null;
}

async function fetchPendencias(
  condominios: Condominio[],
): Promise<{ pendencias: Pendencia[]; indice: number; totalAbertas: number }> {
  const todasPendencias: Pendencia[] = [];

  // For each condominio, run all checks in parallel
  const checkPromises = condominios.map(async (condo) => {
    const condoPendencias: Pendencia[] = [];

    // 1. Registration completeness checks (synchronous)
    const regPendencias = computeRegistrationPendencias(condo);
    condoPendencias.push(...regPendencias);

    // 2. Parallel Supabase queries for this condominio
    const [torresRes, alvarasRes, infraRes, funcRes] = await Promise.all([
      supabase
        .from('torres')
        .select('id', { count: 'exact', head: true })
        .eq('condominio_id', condo.id),
      supabase
        .from('alvaras_condominio')
        .select('id, nome, categoria, status, data_vencimento')
        .eq('condominio_id', condo.id),
      supabase
        .from('infraestrutura')
        .select('id, nome, categoria, status, proxima_manutencao')
        .eq('condominio_id', condo.id),
      supabase
        .from('funcionarios')
        .select('id, nome, funcao, treinamentos')
        .eq('condominio_id', condo.id),
    ]);

    const torresCount = torresRes.count || 0;
    const alvaras = alvarasRes.data || [];
    const infraItems = infraRes.data || [];
    const funcionarios = funcRes.data || [];

    // Torres
    const torresPend = computeTorresPendencia(condo, torresCount);
    if (torresPend) condoPendencias.push(torresPend);

    // Alvarás
    if (alvaras.length === 0) {
      condoPendencias.push({
        id: `alvara-none-${condo.id}`,
        titulo: `Nenhum alvará cadastrado (${condo.nome})`,
        descricao:
          'Cadastre AVCB, licenças do corpo de bombeiros e demais documentos obrigatórios do condomínio.',
        tipo: 'Documentação',
        prioridade: 'alta',
        status: 'pendente',
        acao: 'Cadastrar alvarás',
        link: `/condominios/${condo.id}`,
        condominioNome: condo.nome,
      });
    } else {
      const hoje = new Date();
      const trintaDias = new Date();
      trintaDias.setDate(trintaDias.getDate() + 30);

      for (const alvara of alvaras) {
        if (!alvara.data_vencimento) continue;
        const venc = new Date(alvara.data_vencimento);

        if (venc < hoje) {
          condoPendencias.push({
            id: `alvara-vencido-${alvara.id}`,
            titulo: `${alvara.nome || 'Alvará'} vencido (${condo.nome})`,
            descricao: `Venceu em ${venc.toLocaleDateString('pt-BR')}. Renove o documento para manter a conformidade.`,
            tipo: 'Documentação',
            prioridade: 'alta',
            status: 'pendente',
            acao: 'Renovar documento',
            link: `/condominios/${condo.id}`,
            condominioNome: condo.nome,
          });
        } else if (venc <= trintaDias) {
          condoPendencias.push({
            id: `alvara-proximo-${alvara.id}`,
            titulo: `${alvara.nome || 'Alvará'} próximo do vencimento (${condo.nome})`,
            descricao: `Vence em ${venc.toLocaleDateString('pt-BR')} (${Math.ceil((venc.getTime() - hoje.getTime()) / (1000 * 60 * 60 * 24))} dias). Providencie a renovação.`,
            tipo: 'Documentação',
            prioridade: 'media',
            status: 'pendente',
            acao: 'Providenciar renovação',
            link: `/condominios/${condo.id}`,
            condominioNome: condo.nome,
          });
        }
      }
    }

    // Infraestrutura
    if (infraItems.length === 0) {
      condoPendencias.push({
        id: `infra-none-${condo.id}`,
        titulo: `Nenhum item de infraestrutura cadastrado (${condo.nome})`,
        descricao:
          'Cadastre elevadores, bombas, extintores, geradores e demais equipamentos para controle de manutenção.',
        tipo: 'Manutenção',
        prioridade: 'alta',
        status: 'pendente',
        acao: 'Cadastrar infraestrutura',
        link: `/condominios/${condo.id}`,
        condominioNome: condo.nome,
      });
    } else {
      const hoje = new Date();
      for (const item of infraItems) {
        if (!item.proxima_manutencao) continue;
        const prox = new Date(item.proxima_manutencao);
        if (prox < hoje) {
          condoPendencias.push({
            id: `infra-vencida-${item.id}`,
            titulo: `Manutenção vencida: ${item.nome} (${condo.nome})`,
            descricao: `A manutenção programada venceu em ${prox.toLocaleDateString('pt-BR')}. Agende a execução.`,
            tipo: 'Manutenção',
            prioridade: 'alta',
            status: 'pendente',
            acao: 'Agendar manutenção',
            link: `/condominios/${condo.id}`,
            condominioNome: condo.nome,
          });
        }
      }
    }

    // Funcionários e treinamentos
    if (funcionarios.length === 0) {
      condoPendencias.push({
        id: `func-none-${condo.id}`,
        titulo: `Nenhum funcionário cadastrado (${condo.nome})`,
        descricao:
          'Cadastre os funcionários do condomínio para controle de treinamentos obrigatórios.',
        tipo: 'Segurança',
        prioridade: 'alta',
        status: 'pendente',
        acao: 'Cadastrar funcionários',
        link: `/condominios/${condo.id}`,
        condominioNome: condo.nome,
      });
    } else {
      const hoje = new Date();
      for (const func of funcionarios) {
        const treinamentos = func.treinamentos as Array<{
          nome: string;
          dataConclusao?: string;
          vigenciaDias?: number;
          status?: string;
        }> | null;
        if (!treinamentos || treinamentos.length === 0) {
          condoPendencias.push({
            id: `treino-none-${func.id}`,
            titulo: `Treinamento não registrado: ${func.nome} (${condo.nome})`,
            descricao: `O funcionário ${func.nome} (${func.funcao || 'sem função'}) não possui treinamentos registrados.`,
            tipo: 'Segurança',
            prioridade: 'media',
            status: 'pendente',
            acao: 'Registrar treinamento',
            link: `/dashboard/treinamentos`,
            condominioNome: condo.nome,
          });
        } else {
          for (const treino of treinamentos) {
            // O vencimento nao esta gravado: sai da conclusao mais a vigencia,
            // que e como a tela de funcionarios grava o treinamento. A versao
            // anterior lia `dataValidade`, campo que a aplicacao nunca escreve
            // — nenhum treinamento vencido aparecia aqui, e o painel contava
            // mais pendencias do que esta lista mostrava.
            if (!treino.dataConclusao) continue;

            const val = new Date(
              calcularDataVencimento(treino.dataConclusao, treino.vigenciaDias || 0),
            );
            if (val < hoje) {
              condoPendencias.push({
                id: `treino-vencido-${func.id}-${treino.nome}`,
                titulo: `Treinamento vencido: ${treino.nome} - ${func.nome} (${condo.nome})`,
                descricao: `Venceu em ${val.toLocaleDateString('pt-BR')}. Reagende o treinamento para manter a conformidade.`,
                tipo: 'Segurança',
                prioridade: 'alta',
                status: 'pendente',
                acao: 'Reagendar treinamento',
                link: `/dashboard/treinamentos`,
                condominioNome: condo.nome,
              });
            }
          }
        }
      }
    }

    return condoPendencias;
  });

  const results = await Promise.all(checkPromises);
  results.forEach((p) => todasPendencias.push(...p));

  // Sort by priority: alta > media > baixa
  const prioridadeOrder = { alta: 0, media: 1, baixa: 2 };
  todasPendencias.sort((a, b) => prioridadeOrder[a.prioridade] - prioridadeOrder[b.prioridade]);

  // Compute compliance index
  const totalChecks = condominios.length * 7; // 7 check categories per condominio
  let checksOk = 0;

  for (const condo of condominios) {
    const endereco = condo.endereco as Record<string, unknown> | null;
    if (condo.uso_edificacao) checksOk++;
    if (endereco?.cep && endereco?.logradouro) checksOk++;
    const sindico = condo.sindico as Record<string, unknown> | null;
    if (sindico?.nome && sindico?.email) checksOk++;
    const adm = condo.administradora as Record<string, unknown> | null;
    if (adm?.nome) checksOk++;
    // The remaining 3 checks (torres, alvaras, infra) are counted from pendências
    const hasTorresPend = todasPendencias.some((p) => p.id === `torres-${condo.id}`);
    if (!hasTorresPend) checksOk++;
    const hasAlvaraPend = todasPendencias.some((p) => p.id === `alvara-none-${condo.id}`);
    if (!hasAlvaraPend) checksOk++;
    const hasInfraPend = todasPendencias.some((p) => p.id === `infra-none-${condo.id}`);
    if (!hasInfraPend) checksOk++;
  }

  const indice = totalChecks > 0 ? Math.round((checksOk / totalChecks) * 100) : 0;

  return {
    pendencias: todasPendencias,
    indice,
    totalAbertas: todasPendencias.length,
  };
}

export default function SaudePendenciasPage() {
  const { user, effectiveAccountId } = useAuth();
  const [pendencias, setPendencias] = useState<Pendencia[]>([]);
  const [indiceConformidade, setIndiceConformidade] = useState(0);
  const [totalAbertas, setTotalAbertas] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const carregarDados = useCallback(async () => {
    if (!user) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      let condominiosQuery = supabase
        .from('condominios')
        .select('id, nome, localizacao, uso_edificacao, endereco, sindico, administradora')
        .eq('ativo', true);

      if (effectiveAccountId) {
        condominiosQuery = condominiosQuery.eq('account_id', effectiveAccountId);
      }

      const { data: condominios, error: condoError } = await condominiosQuery;

      if (condoError) throw new Error(condoError.message);

      if (!condominios || condominios.length === 0) {
        setPendencias([]);
        setIndiceConformidade(100);
        setTotalAbertas(0);
        setLoading(false);
        return;
      }

      const result = await fetchPendencias(condominios as Condominio[]);
      setPendencias(result.pendencias);
      setIndiceConformidade(result.indice);
      setTotalAbertas(result.totalAbertas);
    } catch (err) {
      console.error('Erro ao carregar saúde e pendências:', err);
      setError(err instanceof Error ? err.message : 'Erro ao carregar dados.');
    } finally {
      setLoading(false);
    }
  }, [user, effectiveAccountId]);

  useEffect(() => {
    carregarDados();
  }, [carregarDados]);

  return (
    <DashboardLayout>
      <div className="max-w-6xl">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-8">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-foreground-900">
              Saúde e Pendências
            </h1>
            <p className="text-sm text-foreground-600 mt-1">
              Consolidado de pendências obrigatórias de condomínio, torres e imóveis com ações
              diretas para correção.
            </p>
          </div>
          <div className="bg-background-50 border border-background-200/70 rounded-lg p-4 shrink-0">
            <p className="text-xs text-foreground-500 mb-1">Índice de conformidade</p>
            <div className="flex items-baseline gap-2">
              <span className="text-3xl font-bold text-primary-600">{indiceConformidade}%</span>
              <span className="text-sm text-foreground-500">
                {totalAbertas} pendência(s) aberta(s)
              </span>
            </div>
          </div>
        </div>

        {/* Loading */}
        {loading && (
          <div className="flex items-center justify-center py-20">
            <div className="flex flex-col items-center gap-3">
              <div className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
              <p className="text-sm text-foreground-500">Analisando pendências...</p>
            </div>
          </div>
        )}

        {/* Error */}
        {!loading && error && (
          <div className="bg-background-50 border border-background-200/70 rounded-lg p-8 text-center">
            <div className="w-12 h-12 mx-auto mb-4 flex items-center justify-center">
              <i className="ri-error-warning-line text-3xl text-accent-500"></i>
            </div>
            <p className="text-foreground-800 font-medium mb-1">Erro ao carregar dados</p>
            <p className="text-sm text-foreground-600 mb-4">{error}</p>
            <button
              onClick={carregarDados}
              className="inline-flex items-center gap-2 px-4 py-2 bg-primary-500 text-background-50 rounded-md text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-refresh-line"></i>
              Tentar novamente
            </button>
          </div>
        )}

        {/* Empty state */}
        {!loading && !error && pendencias.length === 0 && (
          <div className="bg-background-50 border border-background-200/70 rounded-lg p-12 text-center">
            <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center">
              <i className="ri-checkbox-circle-line text-4xl text-primary-500"></i>
            </div>
            <h2 className="text-lg font-semibold text-foreground-900 mb-2">Tudo em dia!</h2>
            <p className="text-sm text-foreground-600 max-w-md mx-auto">
              Nenhuma pendência encontrada. Todos os cadastros, documentos e manutenções estão em
              conformidade.
            </p>
          </div>
        )}

        {/* Pendências table */}
        {!loading && !error && pendencias.length > 0 && (
          <div className="bg-background-50 border border-background-200/70 rounded-lg">
            <div className="px-5 py-4 border-b border-background-200/70">
              <h2 className="text-base font-semibold text-foreground-900">
                Pendências de cadastro e conformidade
              </h2>
              <p className="text-sm text-foreground-600 mt-1">
                Itens que precisam de atenção. Clique em Ação para navegar direto à tela de
                correção.
              </p>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-background-200/70">
                    <th className="text-left text-xs font-semibold text-foreground-500 uppercase tracking-wider px-5 py-3">
                      Título
                    </th>
                    <th className="text-left text-xs font-semibold text-foreground-500 uppercase tracking-wider px-5 py-3 hidden sm:table-cell">
                      Descrição
                    </th>
                    <th className="text-left text-xs font-semibold text-foreground-500 uppercase tracking-wider px-5 py-3">
                      Tipo
                    </th>
                    <th className="text-left text-xs font-semibold text-foreground-500 uppercase tracking-wider px-5 py-3">
                      Prioridade
                    </th>
                    <th className="text-left text-xs font-semibold text-foreground-500 uppercase tracking-wider px-5 py-3">
                      Status
                    </th>
                    <th className="text-right text-xs font-semibold text-foreground-500 uppercase tracking-wider px-5 py-3">
                      Ação
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-background-200/50">
                  {pendencias.map((p) => (
                    <tr key={p.id} className="hover:bg-background-100/50 transition-colors">
                      <td className="px-5 py-4">
                        <p className="text-sm font-medium text-foreground-800">{p.titulo}</p>
                      </td>
                      <td className="px-5 py-4 hidden sm:table-cell">
                        <p className="text-sm text-foreground-600 max-w-xs">{p.descricao}</p>
                      </td>
                      <td className="px-5 py-4">
                        <span className="text-sm text-foreground-700">{p.tipo}</span>
                      </td>
                      <td className="px-5 py-4">
                        <PrioridadeBadge prioridade={p.prioridade} />
                      </td>
                      <td className="px-5 py-4">
                        <StatusBadge status={p.status} />
                      </td>
                      <td className="px-5 py-4 text-right">
                        {p.link ? (
                          <Link
                            to={p.link}
                            className="inline-flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700 font-medium transition-colors whitespace-nowrap"
                          >
                            {p.acao}
                            <i className="ri-arrow-right-line text-xs"></i>
                          </Link>
                        ) : (
                          <span className="text-sm text-foreground-400">{p.acao}</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
